import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  DollarSign, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Filter,
  Calendar,
  Building2,
  TrendingUp,
  TrendingDown,
  Minus,
  Wallet
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { GuestLink, ReservationLink } from '../components/crm/EntityLinks';
import useConfig from '../hooks/useConfig';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { formatCurrency } from '../utils/formatters';

// Supprimé les couleurs hardcodées - utilisation des couleurs de config

export default function PayoutsPage() {
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ platform: '', received: '' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [receiveForm, setReceiveForm] = useState({
    received_amount: '',
    received_date: format(new Date(), 'yyyy-MM-dd'),
    type_flux: 'SOCIETE',
    notes: ''
  });

  // Load flux types and channels from config
  const { options: configOptions } = useConfig(['FLUX_TYPE', 'CHANNEL']);
  const fluxTypes = configOptions.FLUX_TYPE || [];
  const channels = configOptions.CHANNEL || [];

  // Helper to get channel color
  const getChannelColor = (platform) => {
    const channel = channels.find(c => c.code === platform);
    return channel?.color || '#6B7280';
  };

  // Helper to get channel label  
  const getChannelLabel = (platform) => {
    const channel = channels.find(c => c.code === platform);
    return channel?.label || platform;
  };

  useEffect(() => {
    fetchPayouts();
    fetchStats();
  }, [filters, dateFrom, dateTo]);

  const fetchPayouts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.received) params.append('received', filters.received);
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      
      const response = await api.get(`/payouts?${params.toString()}`);
      setPayouts(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/payouts/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const openReceiveDialog = (payout) => {
    setSelectedPayout(payout);
    // Default to expected_amount_mad or expected_amount
    const expectedMad = payout.expected_amount_mad || payout.expected_amount;
    setReceiveForm({
      received_amount: expectedMad,
      received_date: format(new Date(), 'yyyy-MM-dd'),
      type_flux: 'SOCIETE',
      notes: ''
    });
    setShowDialog(true);
  };

  const handleMarkReceived = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/payouts/${selectedPayout.id}/received`, receiveForm);
      toast.success('Versement marqué comme reçu');
      setShowDialog(false);
      fetchPayouts();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUnmarkReceived = async (payoutId) => {
    if (!window.confirm('Annuler la réception de ce versement ?')) return;
    try {
      await api.put(`/payouts/${payoutId}/unreceived`);
      toast.success('Versement marqué comme en attente');
      fetchPayouts();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const isOverdue = (payout) => {
    if (payout.received) return false;
    return isBefore(new Date(payout.expected_date), startOfDay(new Date()));
  };

  return (
    <div className="space-y-6" data-testid="payouts-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold">Versements Plateformes</h1>
        <p className="text-muted-foreground mt-1">Suivi des paiements Booking & Airbnb</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total attendu</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_expected)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total reçu</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_received)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En attente ({stats.pending_count})</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.pending_amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.overdue_count > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${stats.overdue_count > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                  <AlertTriangle className={`w-6 h-6 ${stats.overdue_count > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En retard ({stats.overdue_count})</p>
                  <p className={`text-2xl font-bold ${stats.overdue_count > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {formatCurrency(stats.overdue_amount)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Platform breakdown */}
      {stats?.by_platform && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(stats.by_platform).map(([platform, data]) => (
            <Card key={platform} className="overflow-hidden">
              <div className="h-1" style={{ backgroundColor: getChannelColor(platform) }} />
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    <span className="font-semibold">{getChannelLabel(platform)}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">
                      Reçu: <span className="text-green-600 font-medium">{formatCurrency(data.received)}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      En attente: <span className="text-orange-600 font-medium">{formatCurrency(data.pending)}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtres:</span>
            </div>
            <Select value={filters.platform} onValueChange={(v) => setFilters({ ...filters, platform: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Plateforme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {channels.filter(c => c.metadata?.is_ota).map(channel => (
                  <SelectItem key={channel.code} value={channel.code}>{channel.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.received} onValueChange={(v) => setFilters({ ...filters, received: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="false">En attente</SelectItem>
                <SelectItem value="true">Reçus</SelectItem>
              </SelectContent>
            </Select>
            <div className="h-6 w-px bg-border hidden sm:block" />
            <DateRangeFilter
              startDate={dateFrom}
              endDate={dateTo}
              onStartDateChange={setDateFrom}
              onEndDateChange={setDateTo}
              onReset={() => { setDateFrom(''); setDateTo(''); }}
              showQuickFilters={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payouts List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des versements</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : payouts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Aucun versement trouvé
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Réservation</th>
                    <th>Plateforme</th>
                    <th>Date prévue</th>
                    <th>Montant attendu</th>
                    <th>Montant reçu</th>
                    <th>Écart</th>
                    <th>Flux</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => {
                    const expectedMad = payout.expected_amount_mad || payout.expected_amount;
                    const hasVariance = payout.received && payout.variance !== 0;
                    
                    return (
                      <tr key={payout.id} className={isOverdue(payout) ? 'bg-red-50' : ''} data-testid={`payout-row-${payout.id}`}>
                        <td>
                          <div>
                            <GuestLink guest={payout.reservation?.guest} />
                            <p className="text-xs text-muted-foreground">
                              {payout.reservation?.check_in && format(new Date(payout.reservation.check_in), 'dd/MM/yyyy')}
                              {' → '}
                              {payout.reservation?.check_out && format(new Date(payout.reservation.check_out), 'dd/MM/yyyy')}
                            </p>
                          </div>
                        </td>
                        <td>
                          <Badge 
                            style={{ 
                              backgroundColor: `${getChannelColor(payout.platform)}20`,
                              color: getChannelColor(payout.platform),
                              borderColor: `${getChannelColor(payout.platform)}40`
                            }}
                            className="border"
                          >
                            {getChannelLabel(payout.platform)}
                          </Badge>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            {format(new Date(payout.expected_date), 'dd MMM yyyy', { locale: fr })}
                            {isOverdue(payout) && (
                              <Badge variant="destructive" className="text-xs">En retard</Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <span className="font-semibold">{formatCurrency(payout.expected_amount, payout.currency)}</span>
                            {payout.currency !== 'MAD' && expectedMad && (
                              <p className="text-xs text-muted-foreground">
                                ≈ {formatCurrency(expectedMad, 'MAD')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          {payout.received ? (
                            <span className="font-semibold text-green-600">
                              {formatCurrency(payout.received_amount || payout.received_amount_mad, 'MAD')}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td>
                          {payout.received && payout.variance !== undefined ? (
                            <div className="flex items-center gap-1">
                              {payout.variance > 0 ? (
                                <>
                                  <TrendingUp className="w-4 h-4 text-green-500" />
                                  <span className="text-green-600 font-medium">+{formatCurrency(payout.variance)}</span>
                                </>
                              ) : payout.variance < 0 ? (
                                <>
                                  <TrendingDown className="w-4 h-4 text-red-500" />
                                  <span className="text-red-600 font-medium">{formatCurrency(payout.variance)}</span>
                                </>
                              ) : (
                                <>
                                  <Minus className="w-4 h-4 text-gray-400" />
                                  <span className="text-muted-foreground">0</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td>
                          {payout.received && payout.type_flux ? (
                            <Badge variant="outline" className="text-xs">
                              <Wallet className="w-3 h-3 mr-1" />
                              {fluxTypes.find(f => f.value === payout.type_flux)?.label || payout.type_flux}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td>
                          {payout.received ? (
                            <div>
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reçu
                              </Badge>
                              {payout.received_date && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  le {format(new Date(payout.received_date), 'dd/MM/yyyy')}
                                </p>
                              )}
                            </div>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">
                              <Clock className="w-3 h-3 mr-1" />
                              En attente
                            </Badge>
                          )}
                        </td>
                        <td>
                          {payout.received ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnmarkReceived(payout.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              Annuler
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => openReceiveDialog(payout)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Marquer reçu
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receive Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marquer le versement comme reçu</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMarkReceived} className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Montant attendu</p>
              <p className="text-xl font-bold">
                {formatCurrency(selectedPayout?.expected_amount, selectedPayout?.currency)}
                {selectedPayout?.currency !== 'MAD' && selectedPayout?.expected_amount_mad && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    (≈ {formatCurrency(selectedPayout.expected_amount_mad, 'MAD')})
                  </span>
                )}
              </p>
              {selectedPayout?.exchange_rate && selectedPayout?.exchange_rate !== 1 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Taux: 1 {selectedPayout.currency} = {selectedPayout.exchange_rate} MAD
                </p>
              )}
            </div>
            <div>
              <Label>Montant reçu (MAD)</Label>
              <Input
                type="number"
                step="0.01"
                value={receiveForm.received_amount}
                onChange={(e) => setReceiveForm({ ...receiveForm, received_amount: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Le montant est toujours saisi en dirhams marocains
              </p>
            </div>
            <div>
              <Label>Flux financier</Label>
              <Select 
                value={receiveForm.type_flux} 
                onValueChange={(v) => setReceiveForm({ ...receiveForm, type_flux: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un flux" />
                </SelectTrigger>
                <SelectContent>
                  {fluxTypes.map(flux => (
                    <SelectItem key={flux.value} value={flux.value}>
                      {flux.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date de réception</Label>
              <Input
                type="date"
                value={receiveForm.received_date}
                onChange={(e) => setReceiveForm({ ...receiveForm, received_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={receiveForm.notes}
                onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })}
                placeholder="Référence virement, commentaires..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                Confirmer la réception
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
